import { Grid, Typography } from '@material-ui/core';
import { FormControl, FormLabel } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../../Context/Layout.context';
import { UserPermission } from '../../../enum/userPermissions';
import usePermission from '../../../hooks/usePermission';
import { getContextualHelpList } from '../../../services/contextualHelp/contextualHelp.service';
import { ContextualHelpDetails, ContextualHelpFilter, ContextualHelpFilterElements, ContextualHelpSortBy, ContextualHelpsTableItem } from '../../../services/contextualHelp/contextualHelp.types';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import { CancelButton } from '../../buttons/CancelButton.component';
import { ConfirmButton } from '../../buttons/ConfirmButton.component';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { IndexedStrings, SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import { TextFieldOne } from '../../TextFieldOne/TextFieldOne.component';
import { useStyles } from './ContextualHelpList.styles';
import { EditContextualHelp } from './EditContextualHelp.component';
import { EmptyContextualHelpListTable } from './EmptyContextualHelpList.component';

const columns: SelectableTableColumn[] = [
  {
    id: 'area',
    type: 'text',
    title: 'Obszar aplikacji',
    filterType: 'search',
    sortable: true,
  },
  {
    id: 'section',
    type: 'text',
    title: 'Sekcja',
    filterType: 'search',
    sortable: true,
  },
  {
    id: 'title',
    type: 'text',
    title: 'Tytuł',
    filterType: 'search',
    sortable: true,
  },
  {
    id: 'actions',
    type: 'actions',
    title: 'Akcje',
    filterType: null,
    skipEnterMethod: true,
  },
];

const defaultFilter = {
  title: '',
  area: '',
  section: '',
  active: [true],
};


export const ContextualHelpList: React.FC<{
  onClose: () => void;
  onChange: () => void;
}> = ({ onClose, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showLoader, openAlert } = useLayoutContext();

  const [data, setData] = useState<ContextualHelpsTableItem[]>([]);
  const [filter, setFilter] = useState<ContextualHelpFilter>(defaultFilter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [sortByColumnId, setSortByColumnId] = useState<ContextualHelpSortBy>('area');
  const [isDescending, setIsDescending] = useState<boolean>(false);
  const [noContextualHelps, setNoContextualHelps] = useState<boolean>(false);
  const [isEditContextualHelpModalOpen, setIsEditContextualHelpModalOpen] = useState<boolean>(false);
  const [selectedContextualHelp, setSelectedContextualHelp] = useState<ContextualHelpDetails>();

  const hasAdminSettingsManagePermission = usePermission(UserPermission.AdminSupportManage);

  const titleMaxLength = 32;

  const getActionsMenuIcon = (row: ContextualHelpDetails) => {
    return hasAdminSettingsManagePermission ? <a className={classes.editButton} onClick={() => {
      setSelectedContextualHelp((prevState) => ({
        ...prevState,
        id: row.id,
        title: row.title,
        description: row.description,
        area: row.area,
        section: row.section,
        url: row.url,
      }));
      setIsEditContextualHelpModalOpen(true);
    }}>Edytuj</a> : <></>;
  };

  const isWithoutEmptyMessage = () => {
    return !(!data.length && Object.entries(filter).toString() !== Object.entries(defaultFilter).toString());
  };

  const truncateString = (str: string) => {
  if (str.length > titleMaxLength) {
    return str.slice(0, titleMaxLength) + "...";
  } else {
    return str;
  }
}

  const refreshData = _.debounce((filter: ContextualHelpFilter, signal?: AbortSignal) => {
    if (!isValidFilter) return;
    showLoader(true);
    getContextualHelpList(filter, sortByColumnId, isDescending, signal)
      .then(
        (contextualHelps: ContextualHelpDetails[]) => {
          if (contextualHelps) {
            const tableData: ContextualHelpsTableItem[] = contextualHelps.map((row: ContextualHelpDetails) => ({
              id: row.id.toString(),
              title: truncateString(row.title),
              area: row.area,
              section: row.section,
              actions: getActionsMenuIcon(row),
            }));

            if (
              !contextualHelps.length &&
              Object.entries(filter).toString() === Object.entries(defaultFilter).toString()
            ) {
              setNoContextualHelps(true);
            } else setNoContextualHelps(false);
            setData(tableData);
          }
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  });

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    refreshData(filter, signal);
  }, [filter, isDescending, sortByColumnId]);
  
  const handleOnClose = () => {
    setIsEditContextualHelpModalOpen(false);
    refreshData(filter);
  };

  const handleFilterChange = (name: string, value: unknown) => {
    typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
    const newFilterAttribute: Partial<ContextualHelpFilter> = { [name]: Array.isArray(value) ? value : [value] };
    setFilter((prevState) => {
      return {
        ...prevState,
        ...newFilterAttribute,
      };
    });
  };

  const filterElements: ContextualHelpFilterElements = { };

  const filterTranslated: IndexedStrings = useMemo(() => {
    const result: IndexedStrings = {};
    Object.keys(filter).forEach((key: string) => {
      result[key] = filter[key as keyof ContextualHelpFilter]?.toString() ?? '';
    });
    return result;
  }, [filter]);

  const filterElementsTranslated: { [key: string]: JSX.Element } = {};
  Object.keys(filterElements).forEach((key: string) => {
    filterElementsTranslated[key] = filterElements[key as keyof ContextualHelpFilterElements];
  });

  return (
    <>
    <Grid item xs={12}>
        <Grid item xs={12} className={classes.headerRow}>
          <Grid item xs={6}>
            <Typography className={classes.tabTitle}>Pomoc kontekstowa</Typography>
          </Grid>
        </Grid>
        {noContextualHelps ? (
          <Grid item xs={12} style={{ marginTop: '70px' }}>
            <EmptyContextualHelpListTable
              onClose={() => {
                refreshData(filter);
              }}
            />
          </Grid>
        ) : (
          <>
            <div style={{ width: '100%', height: '450px', overflowY: 'auto' }}>
              <SelectableTable<ContextualHelpsTableItem, ContextualHelpSortBy>
                columns={columns}
                rows={data}
                filter={filterTranslated}
                filterElements={filterElementsTranslated}
                onFilterChange={handleFilterChange}
                sortByProperty={sortByColumnId}
                isDescending={isDescending}
                setIsDescending={setIsDescending}
                setSortByColumn={setSortByColumnId}
                withoutEmptyMessage={isWithoutEmptyMessage()}
                caption="Pomoc kontekstowa"
              />
            </div>
            {isEditContextualHelpModalOpen && (
              <EditContextualHelp
                title={'Edytuj pomoc kontekstową'}
                data={
                  selectedContextualHelp
                    ? selectedContextualHelp
                    : {
                        id: '',
                        title: '',
                        description: '',
                        area: '',
                        section: '',
                        url: '',
                      }
                }
                onCancel={() => {
                  setIsEditContextualHelpModalOpen(false);
                }}
                onClose={handleOnClose}
              />
            )}
          </>
        )}
      </Grid>
    </>
  );
};
